import { useEffect, useState } from "react";
import {
  useGetManufacturerSubmissionQuery,
  useLazyGetAccessSignedUrlQuery,
} from "@/services";
import { useParams } from "react-router-dom";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material";
import {
  Description,
  PictureAsPdf,
  InsertDriveFile,
  Image,
//   Upload,
  Download,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

interface FileUrlData {
  url: string | null;
  error?: boolean;
  heading: string;
  description: string;
  sentDate: string;
}

interface FileUrls {
  [key: string]: FileUrlData;
}

interface DocFile {
  uri: string;
  fileName: string;
  fileType?: string;
}

const FirstSaleValidate = () => {
  const { id } = useParams();
  const clientId = new URLSearchParams(window.location.search).get("clientId");
  const tenantId = new URLSearchParams(window.location.search).get("tenantId");
  const [fileUrls, setFileUrls] = useState<FileUrls>({});
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState<DocFile | null>(null);
  const [documentTitle, setDocumentTitle] = useState("Select a document");
  // Use iframe for PDFs to avoid worker issues
  const [useIframe, setUseIframe] = useState<boolean>(false);

  const {
    data: manufacturerSubmissionData,
    isLoading: isManufacturerSubmissionLoading,
  } = useGetManufacturerSubmissionQuery({
    clientId: clientId || "",
    ...(tenantId && { tenantId: tenantId }),
    manufacturerId: id || "",
  });
  const QuestionsData =
    manufacturerSubmissionData?.[0]?.supplierResponse?.step2?.responses?.filter(
      (item) => item.response === "Yes" || item.response === "No"
    );
  const [getAccessSignedUrl] = useLazyGetAccessSignedUrlQuery();

  // Function to fetch all signed URLs at once
  const fetchAllSignedUrls = async () => {
    if (!manufacturerSubmissionData || manufacturerSubmissionData.length === 0)
      return;

    const responses =
      manufacturerSubmissionData[0]?.supplierResponse?.step2?.responses || [];
    if (responses.length === 0) return;

    setIsLoadingFiles(true);
    const validResponses = responses.filter(
      (item) =>
        item.response !== "No" &&
        item.response !== "None" &&
        item.response !== "Yes" &&
        item.response !== "null"
    );

    try {
      const urlPromises = validResponses.map(async (item) => {
        const fileName = item.response;
        try {
          // Updated to match your API response structure
          const result = await getAccessSignedUrl({
            fileName,
          }).unwrap();

          // Extract the signed URL directly from result
          const signedUrl = result.signedUrl;

          return {
            fileName,
            signedUrl,
            heading: item.heading,
            description: item.description,
            sentDate: item.sentDate,
          };
        } catch (error) {
          console.error(`Error getting signed URL for ${fileName}:`, error);
          return {
            fileName,
            signedUrl: null,
            error: true,
            heading: item.heading,
            description: item.description,
            sentDate: item.sentDate,
          };
        }
      });

      const results = await Promise.all(urlPromises);

      const urlMap = results.reduce((acc, item) => {
        acc[item.fileName] = {
          url: item.signedUrl,
          error: item.error || false,
          heading: item.heading,
          description: item.description,
          sentDate: item.sentDate?.toString(), // Convert Date to string
        };
        return acc;
      }, {} as FileUrls);
      setFileUrls(urlMap);

      // Set first file as selected if available
      if (results.length > 0 && results[0].signedUrl) {
        const isPdf = results[0].fileName.toLowerCase().endsWith(".pdf");
        setUseIframe(isPdf);

        setSelectedFile({
          uri: results[0].signedUrl,
          fileName: results[0].fileName,
          fileType: getFileType(results[0].fileName),
        });
        setDocumentTitle(results[0].heading || results[0].fileName);
      }
    } catch (error) {
      console.error("Error fetching signed URLs:", error);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  // Helper function to get file icon based on file extension
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    if (extension === "pdf") {
      return <PictureAsPdf color="error" />;
    } else if (["jpg", "jpeg", "png", "gif"].includes(extension)) {
      return <Image color="primary" />;
    } else if (["xlsx", "xls", "csv"].includes(extension)) {
      return <InsertDriveFile color="success" />;
    } else {
      return <Description color="action" />;
    }
  };

  // Helper function to get file type (for DocViewer)
  const getFileType = (fileName: string): string => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    if (extension === "pdf") {
      return "application/pdf";
    } else if (extension === "png") {
      return "image/png";
    } else if (extension === "jpg" || extension === "jpeg") {
      return "image/jpeg";
    } else if (extension === "gif") {
      return "image/gif";
    } else if (extension === "xlsx" || extension === "xls") {
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    } else if (extension === "csv") {
      return "text/csv";
    } else {
      return "application/octet-stream";
    }
  };

  // Helper function to display date in formatted way
  const displayDate = (dateString: Date) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString; // Fallback to original string if parsing fails
    }
  };

  // Handle file selection
  const handleFileClick = async (fileName: string) => {
    const fileData = fileUrls[fileName];

    if (fileData && fileData.url) {
      // Determine if this is a PDF
      const isPdf = fileName.toLowerCase().endsWith(".pdf");
      setUseIframe(isPdf);

      setSelectedFile({
        uri: fileData.url,
        fileName: fileName,
        fileType: getFileType(fileName),
      });
      setDocumentTitle(fileData.heading || fileName);
    }
  };

  // Fetch signed URLs when data is loaded
  useEffect(() => {
    if (manufacturerSubmissionData && manufacturerSubmissionData.length > 0) {
      fetchAllSignedUrls();
    }
  }, [manufacturerSubmissionData, id]);

  if (isManufacturerSubmissionLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading manufacturer data...
        </Typography>
      </Box>
    );
  }

  // Get files from the response
  const getDocumentsList = () => {
    if (
      !manufacturerSubmissionData ||
      manufacturerSubmissionData.length === 0
    ) {
      return [];
    }

    const responses =
      manufacturerSubmissionData[0]?.supplierResponse?.step2?.responses || [];
    const validResponses = responses.filter(
      (item) =>
        item.response !== "No" &&
        item.response !== "None" &&
        item.response !== "Yes" &&
        item.response !== "null"
    );
    return validResponses.map((item) => ({
      fileName: item.response,
      heading: item.heading,
      sentDate: item.sentDate,
    }));
  };

  const documents = getDocumentsList();

  // Function to download the current file
  const handleDownload = () => {
    if (selectedFile && selectedFile.uri) {
      window.open(selectedFile.uri, "_blank");
    }
  };

  // Render document viewer based on file type
  const renderDocumentViewer = () => {
    if (!selectedFile) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="500px"
          sx={{ border: "1px dashed #9e9e9e", borderRadius: 1 }}
        >
          <Typography sx={{ color: tokens.text.secondary }}>
            Select a document to view
          </Typography>
        </Box>
      );
    }

    const isPdf = selectedFile.fileName.toLowerCase().endsWith(".pdf");

    // For PDFs or if we're using the iframe fallback
    if (useIframe || isPdf) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflow: "hidden", // Prevent double scrollbars
            position: "relative",
          }}
        >
          <iframe
            src={selectedFile.uri}
            width="100%"
            height="100%"
            style={{
              border: "none",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            title={selectedFile.fileName}
          />
        </Box>
      );
    }

    // For non-PDFs, use DocViewer
    return (
      <DocViewer
        documents={[selectedFile]}
        pluginRenderers={DocViewerRenderers}
        config={{
          header: {
            disableHeader: true,
            disableFileName: true,
            retainURLParams: false,
          },
        }}
        style={{ height: "100%", width: "100%" }}
      />
    );
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left sidebar - Documents list */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Documents
            </Typography>
            <List>
              {documents.map((doc) => (
                <Box key={doc.fileName}>
                  <ListItem
                    component="div"
                    onClick={() => handleFileClick(doc.fileName)}
                    selected={selectedFile?.fileName === doc.fileName}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      border:
                        selectedFile && selectedFile.fileName === doc.fileName
                          ? "1px solid #1cb1a1 !important"
                          : "1px solid #e0e0e0",
                      backgroundColor:
                        selectedFile && selectedFile.fileName === doc.fileName
                          ? `${tokens.status.successBg} !important`
                          : "white",
                      cursor: "pointer", // Add pointer cursor for better UX
                      flexDirection: "column", // Stack content vertically
                      alignItems: "flex-start", // Align items to the start
                      padding: "8px 16px", // Add some padding
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: "40px" }}>
                        {getFileIcon(doc.fileName)}
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.heading}
                        secondary={`Added ${displayDate(doc.sentDate)}`}
                        primaryTypographyProps={{
                          variant: "body2",
                          fontWeight:
                            selectedFile &&
                            selectedFile.fileName === doc.fileName
                              ? "bold"
                              : "regular",
                          noWrap: true,
                        }}
                      />
                    </Box>

                    {selectedFile && selectedFile.fileName === doc.fileName && (
                      <Typography
                        variant="caption"
                        color="#1cb1a1"
                        sx={{
                          alignSelf: "flex-start",
                          pl: 5, // Align with the text above (accounts for icon width)
                        }}
                      >
                        Currently viewing
                      </Typography>
                    )}
                  </ListItem>
                </Box>
              ))}
              {/* <ListItem
                sx={{
                  borderRadius: 1,
                  border: "1px dashed #9e9e9e",
                  mt: 2,
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <ListItemIcon>
                  <Upload color="action" />
                </ListItemIcon>
                <ListItemText primary="Upload Document" />
              </ListItem> */}
            </List>
          </Paper>
        </Grid>

        {/* Middle section - Document viewer */}
        <Grid item xs={12} md={8} lg={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">{documentTitle}</Typography>
              <Button
                size="small"
                onClick={handleDownload}
                disabled={!selectedFile}
                startIcon={<Download />}
                variant="contained"
                sx={{ minWidth: "auto" }}
              >
                Download
              </Button>
            </Box>

            {isLoadingFiles ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="500px"
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  height: { xs: "min(70vh, 520px)", md: "calc(100vh - 190px)" },
                  border: "1px solid #e0e0e0",
                  position: "relative",
                  overflow: "hidden !important",
                  overflowY: "hidden !important", // Prevent double scrollbars
                }}
              >
                {renderDocumentViewer()}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right sidebar - Validation insights */}
        <Grid item xs={12} md={12} lg={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight={tokens.fontWeight.heavy} gutterBottom p={1}>
              Eligibility Criteria
            </Typography>
            {QuestionsData?.map((item) => (
              <Box key={item.description} p={1}>
                <Typography
                  variant="subtitle2"
                  fontWeight={tokens.fontWeight.heavy}
                  gutterBottom
                >
                  {item.description}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {item.response}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FirstSaleValidate;
